import { LicensePlan } from '../types';
import { PricePoint, PricingInfo, PricingPeriod } from '../types/pricing';

const byEffective = (a: PricePoint, b: PricePoint) =>
  a.effective.localeCompare(b.effective);

/** Distinct effective dates present in a series, ascending. */
const dates = (p: PricingInfo): string[] =>
  [...new Set(p.points.map(x => x.effective))].sort();

/** All points sharing the given effective date (1 normally, 2 for Education). */
const at = (p: PricingInfo, effective: string): PricePoint[] =>
  p.points.filter(x => x.effective === effective).sort(byEffective);

const iso = (on: Date) => on.toISOString().slice(0, 10);

/** The rate in effect today — what a new purchase or a renewal costs. */
export const atRenewalPoints = (p: PricingInfo, on = new Date()): PricePoint[] => {
  const today = iso(on);
  const past = dates(p).filter(d => d <= today);
  return past.length ? at(p, past[past.length - 1]) : at(p, dates(p)[0]);
};

/** The rate a customer is still paying until their term renews. Null when
 *  today's rate is the only one that has ever applied. */
export const preRenewalPoints = (p: PricingInfo, on = new Date()): PricePoint[] | null => {
  const today = iso(on);
  const past = dates(p).filter(d => d <= today);
  return past.length > 1 ? at(p, past[past.length - 2]) : null;
};

/** A published change that has not taken effect yet. Null in a quiet month. */
export const announcedPoints = (p: PricingInfo, on = new Date()): PricePoint[] | null => {
  const today = iso(on);
  const future = dates(p).filter(d => d > today);
  return future.length ? at(p, future[0]) : null;
};

export const announcedDate = (p: PricingInfo, on = new Date()): string | null => {
  const pts = announcedPoints(p, on);
  return pts ? pts[0].effective : null;
};

export const pointsForPeriod = (
  p: PricingInfo,
  period: PricingPeriod,
  on = new Date()
): PricePoint[] => {
  if (period === 'preRenewal') return preRenewalPoints(p, on) ?? atRenewalPoints(p, on);
  if (period === 'announced') return announcedPoints(p, on) ?? atRenewalPoints(p, on);
  return atRenewalPoints(p, on);
};

export const formatPricePoint = (pt: PricePoint): string => {
  if (pt.amount === null) return pt.label ?? 'Contact Sales';
  const money = `$${pt.amount.toFixed(2)} ${pt.unit}`;
  return pt.audience ? `${money} (${pt.audience === 'student' ? 'students' : 'faculty'})` : money;
};

/** Display string for a whole date-slice, joining audience variants. */
export const formatPricePoints = (pts: PricePoint[]): string =>
  pts.map(formatPricePoint).join(' / ');

// ---- Plan-level helpers (what components call) --------------------------

export const getPlanPricing = (plan: LicensePlan): PricingInfo | null =>
  plan.pricing ?? null;

export const getPriceForPeriod = (
  plan: LicensePlan,
  period: PricingPeriod,
  on = new Date()
): string => {
  const p = getPlanPricing(plan);
  if (!p || !p.points.length) return plan.price ?? '$0';
  return formatPricePoints(pointsForPeriod(p, period, on));
};

/** Numeric price for calculators. Returns 0 for non-quotable offers.
 *  Prefer `pointsForPeriod` directly when audience matters. */
export const parsePriceForPeriod = (
  plan: LicensePlan,
  period: PricingPeriod,
  on = new Date()
): number => {
  const p = getPlanPricing(plan);
  if (!p || !p.points.length) return 0;
  const pts = pointsForPeriod(p, period, on);
  return pts[0]?.amount ?? 0;
};

/** True when this plan has a rate customers have not yet felt. */
export const hasPreRenewalGap = (plan: LicensePlan, on = new Date()): boolean => {
  const p = getPlanPricing(plan);
  return !!p && preRenewalPoints(p, on) !== null;
};

export const planAnnouncedDate = (plan: LicensePlan, on = new Date()): string | null => {
  const p = getPlanPricing(plan);
  return p ? announcedDate(p, on) : null;
};
